import apiClient from "./api-client";
import { handleApiError } from "./error-handler";
import {
  FreelancerProposal,
  CreateFreelancerProposalRequest
} from "../models/FreelancerProposal";
import { FreelancerProposalDocument } from "../models/FreelancerProposalDocument";

/**
 * Get the current freelancer's proposal for a specific project
 * @param projectId The project ID
 * @returns The freelancer's proposal or null if none exists
 */
export const getFreelancerProposalByProject = async (projectId: number): Promise<FreelancerProposal | null> => {
  try {
    const response = await apiClient.get(`/freelancer-proposals`, {
      params: { projectId }
    });
    return response.data;
  } catch (error: any) {
    if (error.response?.status === 204) {
      // No proposal exists yet (NoContent)
      return null;
    }
    throw handleApiError(error, 'Error fetching freelancer proposal');
  }
};

/**
 * Create or update a freelancer proposal (draft or final submission based on status)
 * Uses PUT with upsert behavior: creates new proposal or updates existing one
 * @param proposalData Proposal data with status (NotSubmitted = draft, SubmittedCSM = final submission)
 */
export const createFreelancerProposal = async (proposalData: CreateFreelancerProposalRequest): Promise<FreelancerProposal> => {
  try {
    const response = await apiClient.put(`/freelancer-proposals`, proposalData);
    return response.data;
  } catch (error: any) {
    throw handleApiError(error, 'Error creating/updating freelancer proposal');
  }
};

// ===== DOCUMENT MANAGEMENT API CALLS =====

/**
 * Get all documents for a freelancer proposal
 * @param proposalId The proposal ID
 * @returns Array of proposal documents
 */
export const getFreelancerProposalDocuments = async (proposalId: number): Promise<FreelancerProposalDocument[]> => {
  try {
    const response = await apiClient.get(`/freelancer-proposals/${proposalId}/documents`);
    return response.data;
  } catch (error: any) {
    throw handleApiError(error, 'Error fetching proposal documents');
  }
};

/**
 * Upload a document to a freelancer proposal
 * @param proposalId The proposal ID
 * @param document Document data with name and base64 content
 * @returns The created document
 */
export const uploadFreelancerProposalDocument = async (
  proposalId: number,
  document: { name: string; content: string }
): Promise<FreelancerProposalDocument> => {
  try {
    const response = await apiClient.post(`/freelancer-proposals/${proposalId}/documents`, {
      name: document.name,
      content: document.content
    });
    return response.data;
  } catch (error: any) {
    throw handleApiError(error, 'Error uploading proposal document');
  }
};

/**
 * Delete a document from a freelancer proposal
 * @param proposalId The proposal ID
 * @param documentId The document ID
 */
export const deleteFreelancerProposalDocument = async (proposalId: number, documentId: number): Promise<void> => {
  try {
    await apiClient.delete(`/freelancer-proposals/${proposalId}/documents/${documentId}`);
  } catch (error: any) {
    throw handleApiError(error, 'Error deleting proposal document');
  }
};

// ===== PENNYLANE INTEGRATION API CALLS =====

/**
 * Get the Pennylane quote PDF URL for a proposal
 * This endpoint is restricted to internal users (CSM/TAM/Admin)
 * @param proposalId The proposal ID
 * @returns The URL of the quote PDF
 */
export const getPennylaneQuotePdfUrl = async (proposalId: number): Promise<string> => {
  try {
    const response = await apiClient.get(`/billing/proposals/${proposalId}/pennylane-quote-pdf`);
    return response.data.pdfUrl;
  } catch (error: any) {
    throw handleApiError(error, 'Error fetching Pennylane quote PDF');
  }
};